import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {finalize, firstValueFrom, forkJoin, Subject, takeUntil} from 'rxjs';
import {Region} from '../../models/region';
import {RegionService} from '../../services/region-service';
import {PlaceService} from '../../services/place.service';
import {Place} from '../../models/place';
import {NgIf} from '@angular/common';
import {PricingBoxesComponent} from '../pricing-boxes/pricing-boxes.component';
import {StorageService} from '../../services/storage-service';
import {SeoService} from '../../services/seo.service';
import {environment} from '../../environments/environment';
import {PromotionService} from '../../services/promotion.service';

const NUM_SPACES = environment.NUM_SPACES;

@Component({
  selector: 'app-splash-regional',
  imports: [
    NgIf,
    RouterLink,
    PricingBoxesComponent
  ],
  templateUrl: './splash-regional.component.html',
  styleUrl: './splash-regional.component.scss'
})
export class SplashRegionalComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  // Region
  isRegionLoading = false
  isRegionLoaded = false
  regionSlug: string | null = null
  region: Region | null = null

  // Places
  isPlacesLoading = false
  isPlacesLoaded = false
  places: Place[] = []

  constructor(private route: ActivatedRoute,
              private router: Router,
              private storageService: StorageService,
              private regionService: RegionService,
              private placeService: PlaceService,
              private promotionService: PromotionService,
              private seoService: SeoService,) {
  }

  ngOnInit() {
    this.route.url.pipe(takeUntil(this.onDestroy$)).subscribe(segments => {
      this.regionSlug = segments[0].path;
      this.storageService.add(StorageService.REGION_SLUG, this.regionSlug)

      forkJoin([
        this.refreshRegion(this.regionSlug),
        this.refreshPlaces(this.regionSlug)
      ]).pipe(takeUntil(this.onDestroy$))
        .subscribe(async results => {
          this.region = results[0]
          this.places = results[1]

          this.refreshSeo()
        });
    });

    // Handle converting ref to promotion code
    this.route.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe(queryParams => {
      const ref = queryParams['ref'];
      if (ref) this.storePromotionCode(ref).finally()
    })
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  async storePromotionCode(ref: string) {
    const regionSlug = await this.regionService.getRegionSlug()
    const promotion = await firstValueFrom(this.promotionService.getByRef(ref, regionSlug).pipe(takeUntil(this.onDestroy$))).catch(() => Promise.resolve(null));

    // Store the ref and promotion code for sign up
    if (ref) this.storageService.add(StorageService.REF, ref)
    if (promotion) this.storageService.add(StorageService.PROMOTION_CODE, promotion.code)

    // Remove the ref from the URL
    this.router.navigate([], {queryParams: {'ref': null}, queryParamsHandling: 'merge'}).finally()
  }

  refreshRegion(regionSlug: string) {
    this.isRegionLoading = true
    return this.regionService.getBySlug(regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRegionLoading = false
        this.isRegionLoaded = true
      }));
  }

  refreshPlaces(regionSlug: string) {
    this.isPlacesLoading = true;
    return this.placeService.findByRegion(regionSlug, false, false, true, 5, 0).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isPlacesLoading = false
        this.isPlacesLoaded = true
      }))
  }

  refreshSeo() {
    if (!this.region) return;
    if (!this.places) return;

    const title = `Croissant | Book a Flexible Workspace in ${this.region.name}`
    const description = `The easiest way to find workspace in ${this.region.name}. One membership unlocks ${NUM_SPACES}+ workspaces across major cities. Book instantly, check in with your phone, and start working immediately.`

    this.seoService.setTitle(title)
    this.seoService.setDescription(description)
    if (this.places.length > 0 && this.places[0].smallImages.length > 0) this.seoService.setImage(this.places[0].smallImages[0].filePath)

  }

}
